import * as React from 'react'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'

export interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode
  suffix?: React.ReactNode
  label?: string
  error?: React.ReactNode
  containerClassName?: string
  labelClassName?: string
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      containerClassName,
      labelClassName,
      className,
      type = 'text',
      icon,
      suffix,
      label,
      error,
      onChange,
      ...props
    },
    ref,
  ) => {
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      // Fire the onChange callback if provided
      if (onChange) {
        onChange(event)
      }
    }

    return (
      <div className={cn('strata:py-2', containerClassName)}>
        {label && (
          <label
            htmlFor={props.id}
            className={cn(
              'strata:block strata:h-4 strata:pb-0.5 strata:@qm/qc:pb-1 strata:text-left strata:text-gray-600 strata:text-xs strata:font-medium strata:leading-none strata:peer-disabled:cursor-not-allowed strata:peer-disabled:opacity-70',
              labelClassName,
            )}
          >
            {label}
          </label>
        )}
        <div className='strata:relative'>
          {icon && (
            <div className='strata:absolute strata:inset-y-0 strata:start-0 strata:flex strata:items-center strata:ps-3 strata:text-gray-600 strata:pointer-events-none'>
              {icon}
            </div>
          )}
          <input
            type={type}
            className={cn(
              'strata:flex strata:h-10 strata:w-full strata:rounded-sm strata:border strata:border-gray-400 strata:bg-background strata:px-3 strata:py-2 strata:text-sm',
              'strata:file:border-0 strata:file:bg-transparent strata:file:text-sm strata:file:font-medium strata:file:text-foreground',
              'strata:placeholder:text-gray-600',
              'strata:disabled:cursor-not-allowed strata:disabled:opacity-50',
              icon && 'strata:ps-8',
              suffix && 'strata:pr-10',
              error
                ? 'strata:border-destructive strata:focus-visible:outline strata:focus-visible:outline-1 strata:focus-visible:outline-destructive strata:focus-visible:outline-offset-[-1px]'
                : 'strata:hover:focus-strata-thin strata:focus-visible:focus-strata-inner strata:ring-offset-background',
              className,
            )}
            ref={ref}
            onChange={handleInputChange}
            {...props}
          />
          {suffix && (
            <div className='strata:absolute strata:inset-y-0 strata:right-0 strata:flex strata:items-center strata:pr-3 strata:text-gray-600 strata:pointer-events-none'>
              {suffix}
            </div>
          )}
        </div>
        {error && (
          <p
            className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'
            role='alert'
            aria-live='polite'
          >
            {error}
          </p>
        )}
      </div>
    )
  },
)
TextField.displayName = 'TextField'

export { TextField }
